import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class UnitOfMeasureReadOnly extends ModelObject<UnitOfMeasureReadOnly> {

    /**
    * Identifies if this is the system unit of measure for units, used for all product stock items.
    */
    public isUnits: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.isUnits = jsonSerializedObject.isUnits;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.isUnits = this.isUnits;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class UnitOfMeasure extends ModelObjectWithDirtyCheck<UnitOfMeasure> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _litres: number | null = null;
    /**
    * Litres
    * Greater Than : 0
    */
    public get litres(): number | null { return this._litres }
    public set litres(value: number | null) {
      this._litres = value === undefined ? null : value;
    }
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public readOnly: UnitOfMeasureReadOnly = new UnitOfMeasureReadOnly();
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.litres = jsonSerializedObject.litres == null ? null : jsonSerializedObject.litres;
          this.name = jsonSerializedObject.name;
          this.readOnly = new UnitOfMeasureReadOnly(jsonSerializedObject.readOnly);
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.litres = this.litres == null ? null : this.litres;
        toRet.name = this.name;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "UnitOfMeasure.Id cannot be empty"},
            {field: "litres", rule: (v: any) => v == null || v > 0 || "Litres is not greater than minimum allowed value: 0"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class UnitOfMeasureService extends ServiceBase {

  public async getAllUnitsOfMeasure(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UnitOfMeasure)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.unitsOfMeasure.map((v: any) => new UnitOfMeasure(v));;
    return this.makeJsonRequest("unitOfMeasure/v1/getAllUnitsOfMeasure", req, false, extractor, opts);
  };

  public async getUnitOfMeasure(unitOfMeasureId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<UnitOfMeasure> {
    const req = {
    tenantId:api.tenantId.toString(),
    unitOfMeasureId:unitOfMeasureId.toString(),
    };
    const extractor = (response:any) => new UnitOfMeasure(response.unitOfMeasure);;
    return this.makeJsonRequest("unitOfMeasure/v1/getUnitOfMeasure", req, false, extractor, opts);
  };

  public async getUnitsOfMeasure(unitOfMeasureIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(UnitOfMeasure)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    unitOfMeasureIds:unitOfMeasureIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.unitsOfMeasure.map((v: any) => new UnitOfMeasure(v));;
    return this.makeJsonRequest("unitOfMeasure/v1/getUnitsOfMeasure", req, false, extractor, opts);
  };

  public async saveUnitOfMeasure(unitOfMeasure: UnitOfMeasure, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    unitOfMeasure:unitOfMeasure.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("unitOfMeasure/v1/saveUnitOfMeasure", req, true, extractor, opts);
  };

  public async setUnitOfMeasureIsHidden(unitOfMeasureId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    unitOfMeasureId:unitOfMeasureId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("unitOfMeasure/v1/setUnitOfMeasureIsHidden", req, true, extractor, opts);
  };
}

export const unitOfMeasureService = new UnitOfMeasureService();